/**
 * API contracts for the `picking-shipping` module — zod schemas + inferred types shared by
 * apps/api, apps/web and (via generated types) apps/android.
 *
 * OWNERSHIP: the picking-shipping module agent owns this file exclusively. It is
 * pre-created (and pre-exported from ./index.ts) by the scaffold so filling it
 * in never requires touching a shared file. Import from '@warehouse/shared'.
 *
 * Structure:
 *   1. picking flows      — the drag&drop location sequence that sorts every pick list
 *   2. pick rounds        — built from a sales-order selection, scanner or paper
 *   3. pick tasks         — FEFO-suggested vs actually-scanned, claimed online only
 *   4. staging            — pre-planning moves onto a floor area before the round starts
 *   5. packing/shipments  — pallets, documents, packing slip w/ reception QR
 *   6. shipping           — Web-TA/SpedPoint booking (carrier) and own-truck
 *   7. reception          — the restaurant's `/portal/receive/<qrToken>` confirmation
 *   8. commands           — the CommandBus payloads (identical from web and scanner)
 */
import { z } from 'zod';

import { qtySchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// 1. picking flows
// ---------------------------------------------------------------------------

/**
 * One step of a flow. Exactly ONE of area/shelf/bin is set — mixed granularity
 * is allowed inside a flow ("first all of area A, then shelf 03, then bin
 * B-02-1A"), which is what the drag&drop editor produces.
 */
export const pickingFlowStepInputSchema = z
  .object({
    areaId: uuidSchema.nullish(),
    shelfId: uuidSchema.nullish(),
    binId: uuidSchema.nullish(),
  })
  .refine(
    (s) => [s.areaId, s.shelfId, s.binId].filter((v) => v != null).length === 1,
    'a flow step must reference exactly one of areaId / shelfId / binId',
  );
export type PickingFlowStepInput = z.infer<typeof pickingFlowStepInputSchema>;

export const pickingFlowStepSchema = z.object({
  id: uuidSchema,
  flowId: uuidSchema,
  sort: z.number().int(),
  areaId: uuidSchema.nullable(),
  shelfId: uuidSchema.nullable(),
  binId: uuidSchema.nullable(),
  /** denormalized for the editor: `A`, `A-01`, `A-01-1A` */
  label: z.string(),
  kind: z.enum(['area', 'shelf', 'bin']),
});
export type PickingFlowStep = z.infer<typeof pickingFlowStepSchema>;

export const pickingFlowSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  isDefault: z.boolean(),
  createdBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  steps: z.array(pickingFlowStepSchema),
});
export type PickingFlow = z.infer<typeof pickingFlowSchema>;

export const pickingFlowCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  isDefault: z.boolean().default(false),
  /** Ordered — array position IS the sort order the editor dragged into place. */
  steps: z.array(pickingFlowStepInputSchema).max(500).default([]),
});
export type PickingFlowCreate = z.infer<typeof pickingFlowCreateSchema>;

export const pickingFlowUpdateSchema = pickingFlowCreateSchema.partial();
export type PickingFlowUpdate = z.infer<typeof pickingFlowUpdateSchema>;

/** Everything the drag&drop editor can put in a flow (areas, shelves, bins). */
export const pickingFlowTargetSchema = z.object({
  kind: z.enum(['area', 'shelf', 'bin']),
  id: uuidSchema,
  label: z.string(),
  areaId: uuidSchema.nullable(),
  areaCode: z.string().nullable(),
  shelfId: uuidSchema.nullable(),
  isPickLocation: z.boolean().nullable(),
});
export type PickingFlowTarget = z.infer<typeof pickingFlowTargetSchema>;

// ---------------------------------------------------------------------------
// 2. pick rounds
// ---------------------------------------------------------------------------

export const pickRoundModeSchema = z.enum(['scanner', 'paper']);
export type PickRoundMode = z.infer<typeof pickRoundModeSchema>;

export const pickRoundStatusSchema = z.enum(['planned', 'picking', 'picked', 'packed', 'closed']);
export type PickRoundStatus = z.infer<typeof pickRoundStatusSchema>;

export const pickRoundOrderSchema = z.object({
  orderId: uuidSchema,
  orderNo: z.string(),
  restaurantId: uuidSchema,
  restaurantName: z.string(),
  restaurantCode: z.string(),
  deliveryDate: z.string(),
  status: z.string(),
  lineCount: z.number().int(),
  sort: z.number().int(),
});
export type PickRoundOrder = z.infer<typeof pickRoundOrderSchema>;

export const pickRoundSchema = z.object({
  id: uuidSchema,
  roundNo: z.string(),
  deliveryDate: z.string(),
  flowId: uuidSchema.nullable(),
  flowName: z.string().nullable(),
  mode: pickRoundModeSchema,
  status: pickRoundStatusSchema,
  createdBy: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  taskCount: z.number().int(),
  pickedCount: z.number().int(),
  shortCount: z.number().int(),
  orders: z.array(pickRoundOrderSchema).default([]),
});
export type PickRound = z.infer<typeof pickRoundSchema>;

/**
 * A candidate group for round building: one restaurant × delivery date, with
 * its open orders. The UI selects whole groups (Victor: rounds are grouped per
 * restaurant/delivery date).
 */
export const pickRoundCandidateSchema = z.object({
  restaurantId: uuidSchema,
  restaurantName: z.string(),
  restaurantCode: z.string(),
  deliveryDate: z.string(),
  orderCount: z.number().int(),
  lineCount: z.number().int(),
  orders: z.array(
    z.object({
      orderId: uuidSchema,
      orderNo: z.string(),
      status: z.string(),
      lineCount: z.number().int(),
      isStaffOrder: z.boolean(),
    }),
  ),
});
export type PickRoundCandidate = z.infer<typeof pickRoundCandidateSchema>;

export const pickRoundBuildSchema = z.object({
  deliveryDate: z.iso.date(),
  orderIds: z.array(uuidSchema).min(1).max(500),
  flowId: uuidSchema.nullish(),
  mode: pickRoundModeSchema.default('scanner'),
  /** One round per restaurant instead of one round for the whole selection. */
  splitPerRestaurant: z.boolean().default(false),
});
export type PickRoundBuild = z.infer<typeof pickRoundBuildSchema>;

// ---------------------------------------------------------------------------
// 3. pick tasks
// ---------------------------------------------------------------------------

export const pickTaskStatusSchema = z.enum(['open', 'claimed', 'picked', 'short']);
export type PickTaskStatus = z.infer<typeof pickTaskStatusSchema>;

export const pickTaskSchema = z.object({
  id: uuidSchema,
  roundId: uuidSchema,
  roundNo: z.string(),
  orderId: uuidSchema,
  orderNo: z.string(),
  restaurantId: uuidSchema,
  restaurantName: z.string(),
  orderLineId: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  qtyBase: z.string(),
  pickedQtyBase: z.string(),
  status: pickTaskStatusSchema,
  shortageReason: z.string().nullable(),
  sortKey: z.number().int(),

  suggestedBinId: uuidSchema.nullable(),
  suggestedBinCode: z.string().nullable(),
  suggestedPalletId: uuidSchema.nullable(),
  suggestedLpn: z.string().nullable(),
  suggestedLotId: uuidSchema.nullable(),
  suggestedLotNo: z.string().nullable(),
  suggestedBestBefore: z.string().nullable(),

  actualBinId: uuidSchema.nullable(),
  actualBinCode: z.string().nullable(),
  actualPalletId: uuidSchema.nullable(),
  actualLpn: z.string().nullable(),
  actualLotId: uuidSchema.nullable(),
  actualLotNo: z.string().nullable(),

  claimedByUser: uuidSchema.nullable(),
  claimedByDevice: uuidSchema.nullable(),
  claimedAt: z.string().nullable(),
});
export type PickTask = z.infer<typeof pickTaskSchema>;

/** One FEFO allocation line, mirroring `core/stock`'s `PickAllocation`. */
export const pickSuggestionLineSchema = z.object({
  lotId: uuidSchema.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  palletId: uuidSchema.nullable(),
  lpn: z.string().nullable(),
  binId: uuidSchema,
  binCode: z.string(),
  qtyBase: z.string(),
  availableBase: z.string(),
});
export type PickSuggestionLine = z.infer<typeof pickSuggestionLineSchema>;

export const pickSuggestionSchema = z.object({
  requestedBase: z.string(),
  allocatedBase: z.string(),
  shortfallBase: z.string(),
  complete: z.boolean(),
  lines: z.array(pickSuggestionLineSchema),
});
export type PickSuggestionResponse = z.infer<typeof pickSuggestionSchema>;

// ---------------------------------------------------------------------------
// 4. pre-planning / staging
// ---------------------------------------------------------------------------

/**
 * A staging suggestion: "this round needs 40 kg of X, and it currently lives in
 * these bins — move it to the floor". Confirmed as `pick.stage` commands, which
 * are ordinary internal movements through StockLedgerService.
 */
export const stagingSuggestionSchema = z.object({
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  requiredBase: z.string(),
  stagedBase: z.string(),
  lines: z.array(pickSuggestionLineSchema),
});
export type StagingSuggestion = z.infer<typeof stagingSuggestionSchema>;

export const stagingPlanSchema = z.object({
  roundId: uuidSchema,
  stagingBinId: uuidSchema.nullable(),
  stagingBinCode: z.string().nullable(),
  items: z.array(stagingSuggestionSchema),
});
export type StagingPlan = z.infer<typeof stagingPlanSchema>;

// ---------------------------------------------------------------------------
// 5. packing / shipments
// ---------------------------------------------------------------------------

export const shipmentTypeSchema = z.enum(['carrier', 'own']);
export type ShipmentType = z.infer<typeof shipmentTypeSchema>;

export const shipmentStatusSchema = z.enum([
  'draft',
  'booked',
  'shipped',
  'delivered',
  'cancelled',
]);
export type ShipmentStatus = z.infer<typeof shipmentStatusSchema>;

export const shipmentPalletSchema = z.object({
  id: uuidSchema,
  palletId: uuidSchema,
  lpn: z.string(),
  ssccAlias: z.string().nullable(),
  weight: z.string().nullable(),
  volume: z.string().nullable(),
  packageCount: z.number().int(),
});
export type ShipmentPallet = z.infer<typeof shipmentPalletSchema>;

export const shipmentDocumentSchema = z.object({
  id: uuidSchema,
  kind: z.enum(['label', 'receipt']),
  format: z.enum(['pdf', 'zpl']),
  fileName: z.string(),
  sizeBytes: z.number().int(),
  createdAt: z.string(),
});
export type ShipmentDocument = z.infer<typeof shipmentDocumentSchema>;

export const shipmentSchema = z.object({
  id: uuidSchema,
  shipmentNo: z.string(),
  orderId: uuidSchema,
  orderNo: z.string(),
  orderStatus: z.string(),
  restaurantId: uuidSchema,
  restaurantName: z.string(),
  restaurantCode: z.string(),
  deliveryDate: z.string(),
  type: shipmentTypeSchema,
  status: shipmentStatusSchema,
  templateName: z.string().nullable(),
  consignmentNo: z.string().nullable(),
  trackingUrl: z.string().nullable(),
  pickupDate: z.string().nullable(),
  palletCountEstimate: z.number().int().nullable(),
  qrToken: z.string(),
  /** absolute deep link printed as a QR on the packing slip / pallet label */
  receiveUrl: z.string(),
  bookedAt: z.string().nullable(),
  shippedAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  pallets: z.array(shipmentPalletSchema).default([]),
  documents: z.array(shipmentDocumentSchema).default([]),
});
export type Shipment = z.infer<typeof shipmentSchema>;

/**
 * The four carrier templates, verbatim from the old WMS
 * (`shipmentTemplates.ts`). `default-template` means "no carrier template" —
 * the env-configured transport mode/code are sent instead.
 */
export const SHIPMENT_DEFAULT_TEMPLATE = 'default-template';
export const SHIPMENT_TEMPLATES = [
  SHIPMENT_DEFAULT_TEMPLATE,
  'Kyl & Frysexpressen Fryst',
  'Kyl & Frysexpressen Kylt',
  'Kyl & Frysexpressen Torrt',
] as const;
export type ShipmentTemplateName = (typeof SHIPMENT_TEMPLATES)[number];

export const shipmentCreateSchema = z.object({
  orderId: uuidSchema,
  type: shipmentTypeSchema.default('carrier'),
  /** Omit to let the server derive it from the order's article categories. */
  templateName: z.string().max(120).nullish(),
  pickupDate: z.iso.date().nullish(),
  /** Pre-booking (Victor Q20): an editable estimate before packing completes. */
  palletCountEstimate: z.number().int().min(1).max(99).nullish(),
});
export type ShipmentCreate = z.infer<typeof shipmentCreateSchema>;

export const shipmentUpdateSchema = z.object({
  templateName: z.string().max(120).nullish(),
  pickupDate: z.iso.date().nullish(),
  palletCountEstimate: z.number().int().min(1).max(99).nullish(),
  type: shipmentTypeSchema.optional(),
});
export type ShipmentUpdate = z.infer<typeof shipmentUpdateSchema>;

/** Assign a pallet by scanned code (LPN or supplier SSCC) or by id. */
export const shipmentPalletAssignSchema = z
  .object({
    palletId: uuidSchema.optional(),
    /** `LP0…`/`LP1…` or an SSCC alias — whatever the scanner read. */
    code: z.string().trim().min(1).max(64).optional(),
    weight: z.string().max(20).nullish(),
    volume: z.string().max(20).nullish(),
    packageCount: z.number().int().min(1).max(999).default(1),
  })
  .refine((v) => Boolean(v.palletId ?? v.code), 'palletId or code is required');
export type ShipmentPalletAssign = z.infer<typeof shipmentPalletAssignSchema>;

/** Line of the A4 packing slip / pick list document model. */
export const documentLineSchema = z.object({
  sortKey: z.number().int(),
  binCode: z.string().nullable(),
  articleNo: z.string(),
  articleName: z.string(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  lpn: z.string().nullable(),
  qtyBase: z.string(),
  unitCode: z.string(),
  orderNo: z.string(),
  restaurantName: z.string(),
});
export type DocumentLine = z.infer<typeof documentLineSchema>;

/**
 * The paper pick list, in the SAME order as the scanner tasks (that is the
 * whole point of the hybrid design — ARCHITECTURE §8.2). Rendered to A4 PDF by
 * the print module; also returned as JSON so the web can show/print it.
 */
export const pickListDocumentSchema = z.object({
  roundId: uuidSchema,
  roundNo: z.string(),
  deliveryDate: z.string(),
  mode: pickRoundModeSchema,
  flowName: z.string().nullable(),
  printedAt: z.string(),
  restaurants: z.array(z.string()),
  lines: z.array(documentLineSchema),
});
export type PickListDocument = z.infer<typeof pickListDocumentSchema>;

export const packingSlipDocumentSchema = z.object({
  shipmentId: uuidSchema,
  shipmentNo: z.string(),
  orderNo: z.string(),
  restaurantName: z.string(),
  restaurantCode: z.string(),
  deliveryAddress: z.array(z.string()),
  deliveryDate: z.string(),
  printedAt: z.string(),
  /** QR payload — the reception deep link. Rendered as a QR by the printer. */
  qrUrl: z.string(),
  pallets: z.array(z.object({ lpn: z.string(), packageCount: z.number().int() })),
  lines: z.array(documentLineSchema),
});
export type PackingSlipDocument = z.infer<typeof packingSlipDocumentSchema>;

// ---------------------------------------------------------------------------
// 6. reception (spec §5.18)
// ---------------------------------------------------------------------------

export const receptionEventKindSchema = z.enum([
  'confirmed',
  'partial',
  'claim_registered',
  'damaged',
]);
export type ReceptionEventKind = z.infer<typeof receptionEventKindSchema>;

export const receptionLineSchema = z.object({
  orderLineId: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  qtyBase: z.string(),
  unitCode: z.string(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
});
export type ReceptionLine = z.infer<typeof receptionLineSchema>;

export const receptionViewSchema = z.object({
  shipmentId: uuidSchema,
  shipmentNo: z.string(),
  qrToken: z.string(),
  orderNo: z.string(),
  restaurantId: uuidSchema,
  restaurantName: z.string(),
  deliveryDate: z.string(),
  status: shipmentStatusSchema,
  shippedAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  pallets: z.array(z.object({ lpn: z.string(), packageCount: z.number().int() })),
  lines: z.array(receptionLineSchema),
  events: z.array(
    z.object({
      id: uuidSchema,
      kind: receptionEventKindSchema,
      createdAt: z.string(),
      payload: z.unknown(),
    }),
  ),
  /** true once a `confirmed` or `partial` event exists */
  isConfirmed: z.boolean(),
});
export type ReceptionView = z.infer<typeof receptionViewSchema>;

export const receptionClaimReasonSchema = z.enum([
  'missing',
  'damaged',
  'spoiled',
  'cold_chain',
  'received_more',
  'wrong_item',
  'quality',
  'other',
]);
export type ReceptionClaimReason = z.infer<typeof receptionClaimReasonSchema>;

export const receptionClaimLineSchema = z.object({
  orderLineId: uuidSchema.nullish(),
  articleId: uuidSchema,
  qtyBase: qtySchema,
  reason: receptionClaimReasonSchema,
  description: z.string().max(2000).default(''),
});
export type ReceptionClaimLine = z.infer<typeof receptionClaimLineSchema>;

// ---------------------------------------------------------------------------
// 7. commands (CommandBus payloads — identical from web and scanner)
// ---------------------------------------------------------------------------

/**
 * Command types registered with the CommandBus registry. Registering them means
 * the SAME payload works over `POST /api/sync/commands` from the Android app
 * and over the module's REST endpoints from the web (ARCHITECTURE §6).
 */
export const PICKING_COMMAND_TYPES = {
  roundBuild: 'pick.buildRound',
  roundStart: 'pick.startRound',
  roundClose: 'pick.closeRound',
  stage: 'pick.stage',
  claim: 'pick.claim',
  /** Scanner alias of `claim` that names a whole round (`{roundId}`). */
  claimRound: 'pick.claimRound',
  release: 'pick.release',
  confirm: 'pick.confirm',
  short: 'pick.short',
  /** All tasks settled — hand the round back for packing (status `picked`). */
  roundComplete: 'pick.roundComplete',
  packAssign: 'pack.assignPallet',
  packRemove: 'pack.removePallet',
  packComplete: 'pack.complete',
  shipmentCreate: 'shipment.create',
  shipmentUpdate: 'shipment.update',
  shipmentBook: 'shipment.book',
  shipmentCancel: 'shipment.cancel',
  shipmentShip: 'shipment.ship',
  receptionConfirm: 'reception.confirm',
  receptionClaim: 'reception.claim',
} as const;
export type PickingCommandType =
  (typeof PICKING_COMMAND_TYPES)[keyof typeof PICKING_COMMAND_TYPES];

/**
 * Claim work. Two shapes, one handler:
 *
 *  - `{taskIds}` — the web's line-by-line claim;
 *  - `{roundId}` — the scanner's "I am taking this round" (`pick.claimRound`),
 *    which expands to every OPEN task of the round server-side. The device
 *    cannot enumerate the task ids it is claiming without racing the very
 *    conflict claiming exists to prevent, so the round is the unit it names.
 *
 * `deviceId` is accepted and ignored: the claiming device is the SESSION's
 * device (`CommandEnvelope.deviceId` / the actor), never a value the payload
 * gets to assert.
 */
export const pickClaimPayloadSchema = z
  .object({
    taskIds: z.array(uuidSchema).max(200).optional(),
    roundId: uuidSchema.optional(),
    deviceId: uuidSchema.nullish(),
  })
  .refine((v) => (v.taskIds && v.taskIds.length > 0) || Boolean(v.roundId), {
    message: 'taskIds or roundId is required',
    path: ['taskIds'],
  });
export type PickClaimPayload = z.infer<typeof pickClaimPayloadSchema>;

/**
 * `pick.roundComplete` — the scanner handing a finished round back for packing.
 *
 * Distinct from `pick.closeRound` (status `closed`, the supervisor's "this is
 * over"): completing means every task is settled and the round moves to
 * `picked`, which is what unlocks packing. `taskCount` is the device's own
 * count, checked against the server's and reported as a heal when they differ —
 * a scanner that thinks the round had 12 lines when it has 14 has stale data
 * and the operator must hear about it.
 */
export const pickRoundCompletePayloadSchema = z.object({
  roundId: uuidSchema,
  taskCount: z.number().int().min(0).max(10000).nullish(),
});
export type PickRoundCompletePayload = z.infer<typeof pickRoundCompletePayloadSchema>;

export const pickRoundCompleteResultSchema = z.object({
  roundId: uuidSchema,
  status: pickRoundStatusSchema,
  taskCount: z.number().int(),
  pickedCount: z.number().int(),
  shortCount: z.number().int(),
  openCount: z.number().int(),
});
export type PickRoundCompleteResult = z.infer<typeof pickRoundCompleteResultSchema>;

export const pickReleasePayloadSchema = pickClaimPayloadSchema;
export type PickReleasePayload = z.infer<typeof pickReleasePayloadSchema>;

/**
 * What the picker ACTUALLY took. The suggestion is advisory: the bin/pallet/lot
 * recorded here are the scanned ones even when they differ, because that is
 * what makes "which batch went to which restaurant" answerable.
 */
export const pickConfirmPayloadSchema = z.object({
  taskId: uuidSchema,
  binId: uuidSchema.nullish(),
  /** Scanned bin code — resolved server-side when `binId` is absent. */
  binCode: z.string().trim().max(40).nullish(),
  palletId: uuidSchema.nullish(),
  /** Scanned LPN or SSCC alias. */
  palletCode: z.string().trim().max(64).nullish(),
  lotId: uuidSchema.nullish(),
  lotNo: z.string().trim().max(80).nullish(),
  qtyBase: qtySchema,
});
export type PickConfirmPayload = z.infer<typeof pickConfirmPayloadSchema>;

export const pickShortPayloadSchema = z.object({
  taskId: uuidSchema,
  /** What could be picked (may be 0). The rest is the shortage. */
  pickedQtyBase: qtySchema.default('0'),
  reason: z.string().trim().min(1).max(200),
  binId: uuidSchema.nullish(),
  binCode: z.string().trim().max(40).nullish(),
  palletId: uuidSchema.nullish(),
  palletCode: z.string().trim().max(64).nullish(),
  lotId: uuidSchema.nullish(),
});
export type PickShortPayload = z.infer<typeof pickShortPayloadSchema>;

/** Pre-planning: move a pallet (or loose goods) onto the staging floor bin. */
export const pickStagePayloadSchema = z
  .object({
    roundId: uuidSchema,
    toBinId: uuidSchema.nullish(),
    toBinCode: z.string().trim().max(40).nullish(),
    /** Whole-pallet staging: scan the LPN, no article scope needed. */
    palletId: uuidSchema.nullish(),
    palletCode: z.string().trim().max(64).nullish(),
    /** Loose-goods staging. */
    articleId: uuidSchema.nullish(),
    lotId: uuidSchema.nullish(),
    fromBinId: uuidSchema.nullish(),
    fromBinCode: z.string().trim().max(40).nullish(),
    qtyBase: qtySchema.nullish(),
  })
  .refine(
    (v) => Boolean(v.palletId ?? v.palletCode) || Boolean(v.articleId && v.qtyBase),
    'stage either a pallet (palletId/palletCode) or goods (articleId + qtyBase)',
  );
export type PickStagePayload = z.infer<typeof pickStagePayloadSchema>;

/** Paper rounds: one submit, many actuals, applied through the same commands. */
export const pickRoundConfirmSchema = z.object({
  lines: z
    .array(
      z.object({
        taskId: uuidSchema,
        pickedQtyBase: qtySchema,
        binId: uuidSchema.nullish(),
        palletId: uuidSchema.nullish(),
        lotId: uuidSchema.nullish(),
        shortageReason: z.string().trim().max(200).nullish(),
      }),
    )
    .min(1)
    .max(1000),
});
export type PickRoundConfirm = z.infer<typeof pickRoundConfirmSchema>;

export const packAssignPayloadSchema = z.object({
  shipmentId: uuidSchema,
  palletId: uuidSchema.nullish(),
  code: z.string().trim().max(64).nullish(),
  weight: z.string().max(20).nullish(),
  volume: z.string().max(20).nullish(),
  packageCount: z.number().int().min(1).max(999).default(1),
});
export type PackAssignPayload = z.infer<typeof packAssignPayloadSchema>;

export const packRemovePayloadSchema = z.object({
  shipmentId: uuidSchema,
  palletId: uuidSchema,
});
export type PackRemovePayload = z.infer<typeof packRemovePayloadSchema>;

/** Finish packing an order: order status → `packat`, round → `packed`. */
export const packCompletePayloadSchema = z.object({
  shipmentId: uuidSchema,
});
export type PackCompletePayload = z.infer<typeof packCompletePayloadSchema>;

export const shipmentBookPayloadSchema = z.object({
  shipmentId: uuidSchema,
  templateName: z.string().max(120).nullish(),
  pickupDate: z.iso.date().nullish(),
  palletCountEstimate: z.number().int().min(1).max(99).nullish(),
});
export type ShipmentBookPayload = z.infer<typeof shipmentBookPayloadSchema>;

export const shipmentCancelPayloadSchema = z.object({
  shipmentId: uuidSchema,
  reason: z.string().max(300).nullish(),
});
export type ShipmentCancelPayload = z.infer<typeof shipmentCancelPayloadSchema>;

export const shipmentShipPayloadSchema = z.object({
  shipmentId: uuidSchema,
});
export type ShipmentShipPayload = z.infer<typeof shipmentShipPayloadSchema>;

export const receptionConfirmPayloadSchema = z.object({
  qrToken: z.string().trim().min(4).max(120),
  /** `true` = everything arrived as listed; `false` = partial (see `note`). */
  allOk: z.boolean().default(true),
  damaged: z.boolean().default(false),
  note: z.string().max(2000).nullish(),
});
export type ReceptionConfirmPayload = z.infer<typeof receptionConfirmPayloadSchema>;

export const receptionClaimPayloadSchema = z.object({
  qrToken: z.string().trim().min(4).max(120),
  damaged: z.boolean().default(false),
  note: z.string().max(2000).nullish(),
  lines: z.array(receptionClaimLineSchema).min(1).max(200),
});
export type ReceptionClaimPayload = z.infer<typeof receptionClaimPayloadSchema>;

export const receptionClaimResultSchema = z.object({
  shipmentId: uuidSchema,
  claims: z.array(z.object({ claimId: uuidSchema, claimNo: z.string() })),
});
export type ReceptionClaimResult = z.infer<typeof receptionClaimResultSchema>;

/**
 * Delta resources this module publishes to the Android reference cache
 * (`GET /api/sync/delta/:resource`). The names are the ones
 * `DeltaPuller.Resource` in the scanner app already matches on — renaming one
 * silently stops a scanner from filling that table.
 */
export const PICKING_DELTA_RESOURCES = {
  rounds: 'pick-rounds',
  tasks: 'pick-tasks',
} as const;
